import PriceIcon from '@src/assets/icons/ic_pam_kaina.svg';
import LessonIcon from '@src/assets/icons/ic_pamokos_n.svg';
import UserIcon from '@src/assets/icons/ic_profile.svg';
import PlayIcon from '@src/assets/icons/play.svg';
import CheckoutModalPresenter from '@src/checkout/checkoutModalPresenter';
import CheckoutLessonPresenter from '@src/checkout/lesson/checkoutLessonPresenter';
import Rating from '@src/components/Rating';
import DefaultProfile from '@src/components/common/defaultProfile';
import pageSlugs from '@src/components/layout/pageSlugs';
import PhoneNumberModal from '@src/components/phoneNumberModal';
import { useUser } from '@src/services/userContext';
import TeacherVideoModalPresenter from '@src/teachers/teacherVideoModalPresenter';
import api from '@src/utils/api';
import { Themed } from '@theme-ui/mdx';
import { Link, navigate } from 'gatsby';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Button, Card, Flex, Label } from 'theme-ui';

import FeaturedIcon from '@src/assets/icons/featured.svg';

const isFeaturedStyle = {
  background:
    'linear-gradient(180deg, rgba(255,255,255,1) 45%, rgba(148,224,180,1) 100%);',
};
export const maxTeacherDiscount = 0.85;

const teacherCard = ({ teacher, isListMode }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const user = useUser();
  const checkoutModalPresenter = new CheckoutModalPresenter();
  const checkoutLessonPresenter = new CheckoutLessonPresenter();
  const teacherVideoModalPresenter = new TeacherVideoModalPresenter();

  const teacherPriceFrom = (teacher.priceNumber * maxTeacherDiscount).toFixed(
    2
  );
  const videoUrl =
    teacher.videoUrl && teacher.videoUrl.replace('/videos/', '/video/');
  const LinkToTeachersPage = `/${pageSlugs.teacher[i18n.language]}/${
    teacher.slug
  }`;
  const [isPhoneNumberOpen, setIsPhoneNumberOpen] = useState(false);
  const checkIfUserHasPhone = async () => {
    const response = await api.get(`users/hasPhone/${user.id}`);
    return response;
  };
  const bookALesson = async () => {
    if (!user?.userToken) {
      return navigate(`/${pageSlugs.login[locale]}`);
    }
    const doesUserHavePhone = await checkIfUserHasPhone();
    if (!doesUserHavePhone) {
      setIsPhoneNumberOpen(true);
    } else {
      checkoutLessonPresenter.selectTeacher(teacher);
      checkoutModalPresenter.toggleModal({ type: 'lesson' });
    }
  };
  const savePhone = () => {
    return (
      <PhoneNumberModal
        isOpen={isPhoneNumberOpen}
        onClose={() => {
          setIsPhoneNumberOpen(false);
        }}
        location="teacherCard"
        teacher={teacher}
      />
    );
  };
  if (isListMode) {
    return (
      <Card p={4} mb={3}>
        <Flex
          sx={{
            gap: ['30px', '50px', '70px'],
            flexWrap: ['wrap', 'wrap', 'nowrap', 'nowrap'],
          }}
        >
          <Box sx={{ flexShrink: '1' }}>
            <Box sx={{ position: 'relative' }}>
              <Link
                sx={{
                  display: 'block',
                  position: 'relative',
                  size: ['150px', '150px', '140px', '170px'],
                }}
                to={LinkToTeachersPage}
              >
                {teacher.avatarUrl && (
                  <Avatar
                    size={['150px', '150px', '140px', '170px']}
                    src={teacher.avatarUrl}
                    alt={teacher.firstName}
                  />
                )}

                {!teacher.avatarUrl && (
                  <Flex
                    bg="primaryPaleBg"
                    size={['150px', '150px', '140px', '170px']}
                    sx={{
                      borderRadius: '50%',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <UserIcon
                      sx={{
                        width: '70px',
                        height: '70px',
                      }}
                    />
                  </Flex>
                )}
              </Link>
              {/* <TeacherFavorite /> */}
            </Box>
          </Box>
          <Box sx={{ flexBasis: '510px' }}>
            <Flex
              sx={{
                justifyContent: 'space-between',
                flexDirection: ['column-reverse', 'row'],
                gap: '20px',
              }}
            >
              <Link
                sx={{ textDecoration: 'none', color: 'text' }}
                to={LinkToTeachersPage}
              >
                <Themed.h4 sx={{ marginBottom: 'auto 0' }}>
                  {teacher.firstName}
                </Themed.h4>
              </Link>
              <Rating rating={teacher.rating} total={teacher.ratingCount} />
            </Flex>

            <p sx={{ color: 'gray' }}>{teacher.description}</p>

            <Flex
              sx={{
                justifyContent: 'space-between',
                marginBottom: '20px',
                flexDirection: ['column', 'row'],
                gap: '20px',
              }}
            >
              <Flex
                sx={{
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '5px',
                }}
              >
                <PriceIcon height="25" />
                <Themed.h5 sx={{ margin: '0 ' }}>
                  {` ${t('from')} ${teacherPriceFrom} ${
                    teacher?.currency
                  } / ${t('perHour')}`}
                </Themed.h5>
                <Label>{t('oneLessonPrice')}</Label>
              </Flex>

              {teacher.lessonCount && Number(teacher.lessonCount) > 0 && (
                <Flex
                  sx={{
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: '5px',
                  }}
                >
                  <LessonIcon height="25" />
                  <Themed.h5 sx={{ margin: '0 ' }}>
                    {teacher.lessonCount}
                  </Themed.h5>
                  <Label>{t('completedLessons')}</Label>
                </Flex>
              )}
            </Flex>
            <Button className="w-100" variant="inverted" onClick={bookALesson}>
              {t('bookALesson')}
            </Button>
          </Box>

          {videoUrl && (
            <Box
              sx={{
                marginLeft: 'auto',
                flex: '1',
                minWidth: '200px',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '10px',
                  width: '100%',
                  paddingTop:
                    '56.25%' /* 16:9 Aspect Ratio (divide 9 by 16 = 0.5625) */,
                }}
              >
                <iframe
                  loading="lazy"
                  className="responsive-iframe"
                  width="560"
                  height="315"
                  src={videoUrl}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
            </Box>
          )}
        </Flex>
        {savePhone()}
      </Card>
    );
  } else {
    return (
      <Box className="slide-container" sx={{ padding: '25px' }}>
        <Box
          sx={{
            borderRadius: '20px',
            backgroundColor: 'white',
            padding: '28px',
            boxShadow: '0px 10px 20px #0000001a',
            position: 'relative',
            minHeight: '250px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            ...(teacher.isFeatured ? isFeaturedStyle : {}),
          }}
        >
          {teacher.rating && (
            <Box
              sx={{
                position: ['relative', 'absolute'],
                top: ['auto', '20px'],
                right: ['auto', 4],
                marginBottom: 3,
              }}
            >
              <Rating rating={teacher.rating} total={teacher.ratingCount} />
            </Box>
          )}

          <Box
            sx={{
              position: 'relative',
              size: ['70px', '80px', '90px', '110px'],
            }}
          >
            <Link to={LinkToTeachersPage}>
              <DefaultProfile size={['70px', '80px', '90px', '110px']} />

              {teacher.avatarUrl && (
                <Avatar
                  size={['70px', '80px', '90px', '110px']}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                  }}
                  src={teacher.avatarUrl}
                  alt=""
                />
              )}
              {teacher.isFeatured && (
                <FeaturedIcon
                  alt={t('isFeatured')}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: [50, 55, 65, 77],
                    width: [25, 30, 35, 40],
                    height: [25, 30, 35, 40],
                    filter: 'url(#shadow)',
                  }}
                />
              )}
            </Link>
            {/* <TeacherFavorite /> */}
          </Box>
          <Link
            sx={{ textDecoration: 'none', color: 'text' }}
            to={LinkToTeachersPage}
          >
            <Themed.h4 sx={{ margin: '20px 0 0' }}>
              {teacher.firstName}
            </Themed.h4>
          </Link>
          <p sx={{ color: 'gray', marginBottom: 4 }}>{teacher.description}</p>
          <Box sx={{ marginTop: 'auto' }}>
            <Flex
              sx={{
                justifyContent: 'space-between',
                marginBottom: '20px',
                flexDirection: ['column', 'row'],
                gap: '20px',
              }}
            >
              <Flex
                sx={{
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '5px',
                }}
              >
                <PriceIcon height="25" />
                <Themed.h5 sx={{ margin: '0 ' }}>
                  {` ${t('from')} ${teacherPriceFrom} ${
                    teacher?.currency
                  } / ${t('perHour')}`}
                </Themed.h5>
                <Label>{t('oneLessonPrice')}</Label>
              </Flex>

              {teacher.lessonCount && Number(teacher.lessonCount) > 0 && (
                <Flex
                  sx={{
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: '5px',
                  }}
                >
                  <LessonIcon height="25" />
                  <Themed.h5 sx={{ margin: '0 ' }}>
                    {teacher.lessonCount}
                  </Themed.h5>
                  <Label>{t('completedLessons')}</Label>
                </Flex>
              )}
            </Flex>

            <Flex sx={{ alignItems: 'center', gap: '8px' }}>
              <Button
                sx={{ flex: 1 }}
                className="w-100"
                variant={
                  teacher.isFeatured
                    ? 'featuredWhiteButton'
                    : 'invertedSmallWhite'
                }
                onClick={bookALesson}
              >
                {t('bookALesson')}
              </Button>

              {videoUrl && (
                <Button
                  sx={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    padding: '0 0 0 6px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  variant="invertedSmallWhite"
                  onClick={() =>
                    teacherVideoModalPresenter.toggleModal(videoUrl)
                  }
                >
                  <PlayIcon />
                </Button>
              )}
            </Flex>
          </Box>
        </Box>
        {savePhone()}
      </Box>
    );
  }
};

export default teacherCard;
